import React, { memo, useState, useRef, useEffect, useCallback } from 'react';
import { Deal, DealNote, DealNoteVersion, SalesActivity } from '../../lib/types';
import {
  MessageSquare,
  ClipboardList,
  Phone,
  Mail,
  FileText,
  User,
  Edit as EditIcon,
  History,
  Check,
  X,
  Clock,
  Bold,
  Italic,
  List,
  ListOrdered,
  CalendarIcon as Calendar,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Copy,
  Video,
  CalendarCheck,
  MapPin
} from 'lucide-react';
import { ResponsiveModal } from '../ui/ResponsiveModal';
import { DeleteActivityModal } from '../ui/DeleteActivityModal';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { TaskCompletionModal } from '../ui/TaskCompletionModal';
import { supabase } from '../../lib/supabase';
import { useGoogleCalendar } from '../../hooks/deals/useGoogleCalendar';
import { LinkClickHandler } from '../ui/LinkClickHandler';
import { ExpandableHtmlContent } from '../ui/ExpandableHtmlContent';
import { NoteAttachments } from '../ui/NoteAttachments';
import { useLeadNoteStore } from '../../lib/stores/leadNoteStore';
import { useActivityTypesStore, getActivityTypeLabel } from '../../lib/stores/activityTypesStore';
import { MeetingRecordingCard } from '../meetings/MeetingRecordingCard';
import { WorkflowNoteCard, isWorkflowNote, workflowNoteDate } from '../workflows/WorkflowNoteCard';
import { EmailNoteCard, isEmailNote } from '../leads/EmailNoteCard';
import type { MeetingRecording } from '../../lib/types/meetingRecording';
import { groupItemsByDay } from '../shared/timeline/dayGroups';
import { DaySeparator } from '../shared/timeline/DaySeparator';
import { MessageCircle } from 'lucide-react';
import { useClientWhatsAppSessions } from '../../lib/hooks/useClientWhatsAppSessions';
import { ClientWhatsAppSessionCard } from '../shared/timeline/ClientWhatsAppSessionCard';
import type { ClientWhatsAppSession } from '../../lib/services/clientWhatsAppService';
import type { ComposerDrawerPayload, ComposerMode } from '../shared/timeline/InlineComposer';
import { DealComposerHost } from './DealComposerHost';
import { launchInlineTour } from '../../lib/onboarding/launchInlineTour';
import { isTourSeen } from '../../lib/onboarding/tourSeen';
import { TimelineFooter } from '../shared/timeline/TimelineFooter';
import { CardMedia } from '../shared/timeline/CardMedia';
import { CardActions } from '../shared/timeline/CardActions';
import {
  CARD_CLS,
  CARD_META_CLS,
  mediaTileCls,
  ACTION_BTN_CLS,
  CHIP_NEUTRAL_CLS,
  CHIP_DONE_CLS,
  CHIP_LATE_CLS,
  STATE_PILL_CLS,
  STATE_DOT_FALLBACK,
  OUTCOME_WRAP_CLS,
  OUTCOME_LABEL_CLS,
  formatCompactDate,
  formatTime,
} from '../shared/timeline/cardStyles';

// Colori dei pallini per la pill di cambio stato offerta (stessa palette di
// StatusUpdateDrawer). Gli stati fuori palette (lost, cancelled) usano il grigio
// di fallback STATE_DOT_FALLBACK (M19).
const DEAL_STATUS_DOT: Record<string, string> = {
  draft: '#9B9A97',
  sent: '#2383E2',
  accepted: '#0F7B6C',
  rejected: '#D44C47',
};

// Helper per convertire URL testuali in link HTML cliccabili
const convertUrlsToLinks = (text: string): string => {
  if (!text) return '';
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  return text.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>');
};

interface DealNotesSectionProps {
  deal: Deal | null;
  dealNotes: DealNote[];
  dealActivities?: SalesActivity[]; // New explicit prop for activities
  loadingNotes: boolean;
  translations: any;
  formatDate: (dateString: string) => string;
  onAddNote: () => void;
  onAddTask: () => void;
  onEditNote?: (noteId: string, content: string) => Promise<void>;
  onFetchNoteVersions?: (noteId: string) => Promise<DealNoteVersion[]>;
  onDeleteNote?: (noteId: string) => Promise<void>;
  onEditActivity?: (activity: any) => void;
  onDeleteActivity?: (activityId: string) => Promise<void>;
  onUpdateActivities?: (activities: SalesActivity[]) => void; // Callback to safely update activities
  // Chiamato dopo upload/eliminazione di un allegato, così il parent ricarica
  // le note (gli allegati arrivano via embed note_attachments) e l'allegato non "sparisce"
  onAttachmentsChanged?: () => void;
  toast?: {
    success: (message: string) => void;
    error: (message: string) => void;
    info: (message: string) => void;
    warning: (message: string) => void;
  };
  /**
   * Composer inline sopra la cronologia. Presente SOLO dove il consumer sa ricaricare la
   * timeline dopo un salvataggio e aprire il drawer con il contenuto (`DealDetailsPage`).
   * Le SCRITTURE restano dentro la sezione, sugli stessi percorsi del drawer
   * (`useDealComposerSaves`).
   */
  composer?: {
    onSaved: () => void | Promise<void>;
    onOpenDrawer: (mode: ComposerMode, payload: ComposerDrawerPayload) => void;
  };
  /**
   * true mentre sopra la cronologia e' aperto un pannello (editor offerta, drawer nota/attivita'):
   * il tour `cronologia-redesign` non parte e viene ritentato quando torna false.
   */
  suppressTour?: boolean;
}

const DealNotesSection: React.FC<DealNotesSectionProps> = ({
  deal,
  dealNotes,
  dealActivities = [], // Default to empty array if not provided
  loadingNotes,
  translations: t,
  formatDate,
  onAddNote,
  onAddTask,
  onEditNote,
  onFetchNoteVersions,
  onDeleteNote,
  onEditActivity,
  onDeleteActivity,
  onUpdateActivities,
  onAttachmentsChanged,
  toast = {
    success: (message) => console.log('Toast success:', message),
    error: (message) => console.error('Toast error:', message),
    info: (message) => console.log('Toast info:', message),
    warning: (message) => console.warn('Toast warning:', message)
  },
  composer,
  suppressTour = false
}) => {
  // Function to translate deal status
  const translateStatus = (status: string): string => {
    const statusTranslations: Record<string, string> = {
      draft: t.statusBadge?.draft || 'Bozza',
      sent: t.statusBadge?.sent || 'Inviata',
      accepted: t.statusBadge?.accepted || 'Accettata',
      rejected: t.statusBadge?.rejected || 'Rifiutata',
      lost: 'Persa',
      cancelled: 'Annullata'
    };
    return statusTranslations[status] || status;
  };

  // States for editing
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState<string>('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  
  // States for history modal
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [noteVersions, setNoteVersions] = useState<DealNoteVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  
  // States for delete confirmation modals
  const [showDeleteNoteModal, setShowDeleteNoteModal] = useState(false);
  const [showDeleteActivityModal, setShowDeleteActivityModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  // States for deleting activity with Google Calendar
  const [showDeleteActivityWithCalendarModal, setShowDeleteActivityWithCalendarModal] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<SalesActivity | null>(null);
  const [isDeletingActivity, setIsDeletingActivity] = useState(false);

  // States for activity completion
  const [showTaskCompletionModal, setShowTaskCompletionModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<SalesActivity | null>(null);
  const [isCompletingActivity, setIsCompletingActivity] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(true);

  // Storico WhatsApp (vista 7, BETA): sessioni del cliente collegato all'offerta,
  // prioritizzando i link manuali con deal_id = questa offerta. `deal.client_id`
  // e `deal.lead_id` sono entrambi righe `clients`: si usa il primo disponibile.
  const clientWa = useClientWhatsAppSessions(
    deal?.client_id ?? (deal as any)?.lead_id ?? null,
    deal?.id ?? null,
  );
  const [showWhatsAppLayer, setShowWhatsAppLayer] = useState(true);

  // Google Calendar hook for deleting events
  const { deleteEvent } = useGoogleCalendar();

  // Get attachment functions from store
  const { uploadNoteAttachment, deleteNoteAttachment } = useLeadNoteStore();

  const { customTypes, fetched: activityTypesFetched, fetchCustomTypes } = useActivityTypesStore();

  // Fetch tipi attività custom per etichette badge corrette (evita fallback slug grezzo)
  useEffect(() => {
    if (!activityTypesFetched) fetchCustomTypes();
  }, [activityTypesFetched, fetchCustomTypes]);

  // Wrapper: dopo upload/delete dell'allegato rinfresca le note del parent.
  // uploadNoteAttachment/deleteNoteAttachment aggiornano lo store dei lead-note,
  // NON la prop dealNotes renderizzata qui: senza refresh l'allegato sparisce.
  const handleUploadNoteAttachment = useCallback(async (noteId: string, file: File) => {
    const result = await uploadNoteAttachment(noteId, file);
    if (result) onAttachmentsChanged?.();
    return result;
  }, [uploadNoteAttachment, onAttachmentsChanged]);

  const handleDeleteNoteAttachment = useCallback(async (attachmentId: string) => {
    const result = await deleteNoteAttachment(attachmentId);
    if (result) onAttachmentsChanged?.();
    return result;
  }, [deleteNoteAttachment, onAttachmentsChanged]);

  // Meeting recordings state
  const [dealRecordings, setDealRecordings] = useState<MeetingRecording[]>([]);

  // States for related notes
  const [relatedNotes, setRelatedNotes] = useState<DealNote[]>([]);
  const [relatedActivities, setRelatedActivities] = useState<SalesActivity[]>([]);
  const [loadingRelatedData, setLoadingRelatedData] = useState(false);
  const [showRelatedContent, setShowRelatedContent] = useState(true);
  // Modal registrazione meeting (allineato a LeadNotesSection)
  
  // Stati per l'indicatore di caricamento e la notifica di nuovi contenuti
  const [isCheckingUpdates, setIsCheckingUpdates] = useState(false);
  const [hasNewContent, setHasNewContent] = useState(false);
  const [newNotes, setNewNotes] = useState<DealNote[]>([]);
  const [newActivities, setNewActivities] = useState<SalesActivity[]>([]);
  
  // Ref per tracciare le note e attività già visualizzate
  const processedNoteIdsRef = useRef<Set<string>>(new Set());
  const processedActivityIdsRef = useRef<Set<string>>(new Set());
  const initialDataLoadedRef = useRef<boolean>(false);
  
  // Check if update activities callback is provided
  
  // Initialize editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:text-blue-200 dark:hover:text-blue-300 cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder: t.statusUpdate?.notePlaceholder || 'Scrivi una nota...',
      }),
    ],
    content: editingNoteContent,
    onUpdate: ({ editor }) => {
      setEditingNoteContent(editor.getHTML());
    },
  });
  
  // L'icona eredita il colore dall'iconcina 22px della card (rosa Flusia)
  const getActivityIcon = (activity: any) => {
    if (!activity.type) return <ClipboardList className="h-3 w-3" strokeWidth={1.5} />;

    switch(activity.type) {
      case 'call':
        return <Phone className="h-3 w-3" strokeWidth={1.5} />;
      case 'videochiamata':
        return <Video className="h-3 w-3" strokeWidth={1.5} />;
      case 'appuntamento':
        return <CalendarCheck className="h-3 w-3" strokeWidth={1.5} />;
      case 'sopralluogo':
        return <MapPin className="h-3 w-3" strokeWidth={1.5} />;
      case 'email':
        return <Mail className="h-3 w-3" strokeWidth={1.5} />;
      case 'note':
        return <FileText className="h-3 w-3" strokeWidth={1.5} />;
      case 'task':
      case 'other':
      default:
        return <ClipboardList className="h-3 w-3" strokeWidth={1.5} />;
    }
  };

  // Fetch related notes and activities from lead and client
  // NOTE: deal_notes does NOT have a FK to user_profiles, so we fetch profiles separately
  // (same pattern as leadNoteStore.ts)
  const fetchRelatedData = useCallback(async (dealId?: string, leadId?: string, clientId?: string) => {
    if (!leadId && !clientId) return;

    setLoadingRelatedData(true);
    try {
      const entityId = leadId || clientId;
      const sourceType = leadId ? 'lead' : 'client';
      const sourceLabel = leadId ? 'Lead' : 'Cliente';

      // Fetch notes without FK join (FK doesn't exist for deal_notes -> user_profiles)
      const { data: notesData, error: notesError } = await supabase
        .from('deal_notes')
        .select('*, attachments:note_attachments(*)')
        .eq('lead_id', entityId)
        .order('created_at', { ascending: false });

      let relatedNotesResult: DealNote[] = [];
      if (!notesError && notesData && notesData.length > 0) {
        // Fetch author profiles separately
        const userIds = [...new Set(notesData.map(n => n.user_id).filter(Boolean))];
        let authorsMap: Record<string, any> = {};
        if (userIds.length > 0) {
          const { data: authorsData } = await supabase
            .from('user_profiles')
            .select('id, full_name, email, profile_image_url, avatar_url')
            .in('id', userIds);
          if (authorsData) {
            authorsMap = authorsData.reduce((acc, a) => { acc[a.id] = a; return acc; }, {} as Record<string, any>);
          }
        }

        relatedNotesResult = notesData.map(note => ({
          ...note,
          author: !note.user_id
            ? { id: 'system', email: 'system@flusia.it', full_name: 'Flusia', profile_image_url: 'https://flusia.it/favicon.ico' }
            : authorsMap[note.user_id] || { id: note.user_id, full_name: 'Unknown', email: '' },
          source: sourceType,
          source_label: sourceLabel
        }));
      }

      // Fetch activities (FK joins work fine for sales_activities)
      const { data: activitiesData, error: activitiesError } = await supabase
        .from('sales_activities')
        .select(`
          *,
          users:user_profiles!sales_activities_user_id_fkey1(
            id, email, full_name, profile_image_url, avatar_url
          ),
          assignee:user_profiles!sales_activities_assigned_to_fkey(
            id, email, full_name, profile_image_url, avatar_url
          )
        `)
        .eq('lead_id', entityId)
        .order('created_at', { ascending: false });

      let relatedActivitiesResult: SalesActivity[] = [];
      if (!activitiesError && activitiesData) {
        relatedActivitiesResult = activitiesData.map(activity => ({
          ...activity,
          source: sourceType,
          source_label: sourceLabel
        }));
      }

      setRelatedNotes(relatedNotesResult);
      setRelatedActivities(relatedActivitiesResult);

      console.log(`[DealNotesSection] Found ${relatedNotesResult.length} related notes and ${relatedActivitiesResult.length} related activities`);
    } catch (error) {
      console.error('[DealNotesSection] Error fetching related data:', error);
    } finally {
      setLoadingRelatedData(false);
    }
  }, []); // Empty dependencies since we check ID changes in useEffect with refs
  
  // Start editing a note
  const handleEditNote = (note: DealNote) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content);
    
    // Update editor content if it exists
    if (editor) {
      editor.commands.setContent(note.content);
    }
  };
  
  // Save edited note
  const handleSaveNote = async () => {
    if (!editingNoteId || !onEditNote) return;
    
    setIsSavingNote(true);
    try {
      await onEditNote(editingNoteId, editingNoteContent);
      setEditingNoteId(null);
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setIsSavingNote(false);
    }
  };
  
  // Cancel note editing
  const handleCancelEdit = () => {
    setEditingNoteId(null);
  };
  
  // Show note history
  const handleShowHistory = async (noteId: string) => {
    if (!onFetchNoteVersions) return;
    
    setSelectedNoteId(noteId);
    setShowHistoryModal(true);
    setLoadingVersions(true);
    
    try {
      const versions = await onFetchNoteVersions(noteId);
      setNoteVersions(versions);
    } catch (error) {
      console.error('Error fetching note versions:', error);
      setNoteVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  // Handle activity completion
  const handleActivityCompletion = (activity: SalesActivity) => {
    setSelectedActivity(activity);
    setShowTaskCompletionModal(true);
  };
  
  const submitActivityCompletion = async (activityId: string, outcome: string, createNew: boolean = false) => {
    try {
      console.log("[DealNotesSection] Completing activity:", { activityId, outcome, createNew });
      setIsCompletingActivity(true);
      
      // Update the activity status in the database
      const { error } = await supabase
        .from('sales_activities')
        .update({ 
          status: 'completed',
          outcome: outcome,
          completed_at: new Date().toISOString() 
        })
        .eq('id', activityId);
        
      if (error) {
        throw error;
      }
      
      // If the activity is related to a client, add a note
      if (selectedActivity?.client_id) {
        await supabase
          .from('client_notes')
          .insert({
            client_id: selectedActivity.client_id,
            content: `Attività completata: ${selectedActivity.title}\n\nEsito: ${outcome}`,
            created_by: selectedActivity.user_id,
            organization_id: (await supabase.from('user_profiles').select('organization_id').eq('id', selectedActivity.user_id).single()).data?.organization_id
          });
      }
      
      // Update the local deal activities immediately
      // Update local state only after DB update is successful
      if (deal && deal.activities) {
        try {
          // Create a new activities array with the updated activity
          const updatedActivities = deal.activities.map((act: any) => 
            act.id === activityId 
              ? { 
                  ...act, 
                  status: 'completed', 
                  outcome: outcome, 
                  completed_at: new Date().toISOString() 
                }
              : act
          );
          
          // Update parent component with new activities array
          if (onUpdateActivities) {
            console.log("[DealNotesSection] Calling onUpdateActivities with completed activity");
            onUpdateActivities(updatedActivities);
          } else {
            console.warn("[DealNotesSection] No onUpdateActivities provided, skipping update");
          }
        } catch (e) {
          console.error("[DealNotesSection] Error updating activities:", e);
        }
      }
      
      // If createNew, navigate to create new activity
      if (createNew && deal) {
        onAddTask();
      }
      
      // Reset state
      setShowTaskCompletionModal(false);
      setSelectedActivity(null);
      
      toast.success(`Attività "${selectedActivity?.title}" completata`);

    } catch (error) {
      console.error('[DealNotesSection] Error completing activity:', error);
      toast.error('Errore nel completamento dell\'attività');
    } finally {
      setIsCompletingActivity(false);
    }
  };

  // Log activity and note counts without causing re-renders
  const isInitialRender = useRef(true);
  
  // Fetch related data when deal changes
  // Use refs to track if we already loaded data for these IDs to prevent loops
  const loadedDataRef = useRef<{dealId?: string, leadId?: string, clientId?: string, status?: string}>({});
  
  useEffect(() => {
    if (deal?.id && (deal.lead_id || deal.client_id)) {
      // Only fetch if IDs (or the deal status) actually changed: uscendo da "accettata"
      // il database toglie le task di rinnovo (trg_deals_cleanup_renewal_tasks) e la
      // lista va riletta, altrimenti restavano a video fino al refresh.
      const currentIds = {
        dealId: deal.id,
        leadId: deal.lead_id,
        clientId: deal.client_id,
        status: deal.status
      };
      
      if (loadedDataRef.current.dealId !== currentIds.dealId ||
          loadedDataRef.current.leadId !== currentIds.leadId ||
          loadedDataRef.current.clientId !== currentIds.clientId ||
          loadedDataRef.current.status !== currentIds.status) {
        
        console.log('[DealNotesSection] IDs changed, fetching related data:', currentIds);
        loadedDataRef.current = currentIds;
        fetchRelatedData(currentIds.dealId, currentIds.leadId, currentIds.clientId);
      }
    }
  }, [deal?.id, deal?.lead_id, deal?.client_id, deal?.status]);

  // Fetch meeting recordings for deal + associated lead/client
  // Leads e clients condividono la stessa tabella (clients).
  // Una recording potrebbe avere lead_id O client_id per lo stesso UUID,
  // quindi cerchiamo in ENTRAMBE le colonne per ogni entity ID del deal.
  const fetchDealRecordings = useCallback(async () => {
    if (!deal?.id) return;
    try {
      const conditions = [`deal_id.eq.${deal.id}`];

      const entityIds = new Set<string>();
      if (deal.lead_id) entityIds.add(deal.lead_id);
      if (deal.client_id) entityIds.add(deal.client_id);

      entityIds.forEach(id => {
        conditions.push(`lead_id.eq.${id}`);
        conditions.push(`client_id.eq.${id}`);
      });

      console.log('[DealNotesSection] Fetching recordings with conditions:', conditions.join(','));

      const { data, error } = await supabase
        .from('meeting_recordings')
        .select('*')
        .or(conditions.join(','))
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[DealNotesSection] Error fetching recordings:', error);
        return;
      }

      if (data) {
        // Normalize null arrays to empty arrays and deduplicate
        const uniqueMap = new Map<string, MeetingRecording>();
        data.forEach(rec => uniqueMap.set(rec.id, {
          ...rec,
          participants: rec.participants || [],
          transcript_segments: rec.transcript_segments || [],
          ai_key_points: rec.ai_key_points || [],
          ai_action_items: rec.ai_action_items || [],
        } as MeetingRecording));
        const recordings = Array.from(uniqueMap.values());
        console.log(`[DealNotesSection] Fetched ${recordings.length} recordings`);
        setDealRecordings(recordings);
      }
    } catch (err) {
      console.error('[DealNotesSection] Error fetching recordings:', err);
    }
  }, [deal?.id, deal?.lead_id, deal?.client_id]);

  useEffect(() => {
    fetchDealRecordings();
  }, [fetchDealRecordings]);

  // Realtime subscription for meeting recordings
  useEffect(() => {
    if (!deal?.id) return;
    const channels: any[] = [];

    // Subscribe to deal_id changes
    channels.push(
      supabase.channel(`deal-rec-deal-${deal.id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'meeting_recordings', filter: `deal_id=eq.${deal.id}` }, () => fetchDealRecordings())
        .subscribe()
    );

    // Per ogni entity ID unico, ascoltare sia lead_id che client_id
    const entityIds = new Set<string>();
    if (deal.lead_id) entityIds.add(deal.lead_id);
    if (deal.client_id) entityIds.add(deal.client_id);

    entityIds.forEach(id => {
      channels.push(
        supabase.channel(`deal-rec-lead-${id}`)
          .on('postgres_changes', { event: '*', schema: 'public', table: 'meeting_recordings', filter: `lead_id=eq.${id}` }, () => fetchDealRecordings())
          .subscribe()
      );
      channels.push(
        supabase.channel(`deal-rec-client-${id}`)
          .on('postgres_changes', { event: '*', schema: 'public', table: 'meeting_recordings', filter: `client_id=eq.${id}` }, () => fetchDealRecordings())
          .subscribe()
      );
    });

    return () => { channels.forEach(ch => ch.unsubscribe()); };
  }, [deal?.id, deal?.lead_id, deal?.client_id, fetchDealRecordings]);

  /**
   * Etichetta di provenienza di una registrazione correlata (M16): deriva dai campi della
   * REGISTRAZIONE, non dall'offerta. Un'offerta puo' avere sia lead_id sia client_id: in quel
   * caso la registrazione del cliente non va etichettata "Da: Lead". Lead e clienti condividono
   * la tabella `clients`, quindi per ogni entita' dell'offerta si controllano entrambe le colonne
   * della registrazione. Se la registrazione non e' distinguibile (stesso uuid sulle due colonne,
   * oppure registrazione di un'altra offerta dello stesso cliente) il fallback usa la STESSA
   * regola di note e attivita' correlate (`sourceLabel` in `fetchRelatedData`, :284:
   * `leadId ? 'Lead' : 'Cliente'`): l'offerta con `lead_id === client_id` e' il caso comune
   * (~17% delle offerte in prod), e un fallback fisso a "Cliente" faceva convivere nella stessa
   * cronologia registrazioni "Da: Cliente" e note "Da: Lead" per la stessa entita'.
   */
  const getRecordingSourceLabel = (rec: MeetingRecording): string => {
    const leadId = deal?.lead_id;
    const clientId = deal?.client_id;
    const matchesLead = !!leadId && (rec.lead_id === leadId || rec.client_id === leadId);
    const matchesClient = !!clientId && (rec.lead_id === clientId || rec.client_id === clientId);
    if (matchesLead && !matchesClient) return 'Lead';
    if (matchesClient && !matchesLead) return 'Cliente';
    return leadId ? 'Lead' : 'Cliente';
  };

  /**
   * Il toggle "attivita' completate" filtra sia le attivita' dirette sia quelle correlate:
   * il gating deve guardare la stessa unione, altrimenti il bottone sparisce quando la sola
   * attivita' completata arriva dal lead/cliente (M25).
   */
  const hasCompletedActivities =
    dealActivities.some((act: any) => act.status === 'completed') ||
    relatedActivities.some((act: any) => act.status === 'completed');

  // Tour "cronologia-redesign" (Task 12): stesso specchio della condizione empty-state piu' sotto,
  // ma senza dipendere dalla IIFE di render (i dati derivati li' non sono accessibili qui).
  const hasTimelineItems =
    dealNotes.length > 0 ||
    dealActivities.length > 0 ||
    dealRecordings.length > 0 ||
    relatedNotes.length > 0 ||
    relatedActivities.length > 0;
  const isTimelineLoading = loadingNotes || loadingRelatedData;

  useEffect(() => {
    // Solo dove il composer esiste (non nella tab commerciale del progetto): evita di
    // bruciare il flag del tour su una vista che non mostra il primo step (vedi lead).
    // Con l'editor dell'offerta aperto il composer e' coperto: il tour aspetta la chiusura.
    if (!composer || suppressTour || isTimelineLoading || !hasTimelineItems || isTourSeen('cronologia-redesign')) return;

    const timer = window.setTimeout(() => {
      launchInlineTour('cronologia-redesign');
    }, 400);

    return () => window.clearTimeout(timer);
  }, [composer, suppressTour, isTimelineLoading, hasTimelineItems]);

  // Aggiorna le ref di tracciamento quando cambiano i dati visualizzati
  // Usiamo una ref per memorizzare i conteggi precedenti e evitare aggiornamenti inutili
  const prevCountsRef = useRef<{notes: number, activities: number}>({notes: 0, activities: 0});
  
  useEffect(() => {
    // Esegui solo quando i conteggi cambiano per evitare esecuzioni inutili
    if (dealNotes.length !== prevCountsRef.current.notes || 
        dealActivities.length !== prevCountsRef.current.activities) {
      
      if (dealNotes.length > 0 || dealActivities.length > 0) {
        // Aggiorna la traccia di ciò che è già stato visualizzato
        dealNotes.forEach(note => processedNoteIdsRef.current.add(note.id));
        dealActivities.forEach(activity => processedActivityIdsRef.current.add(activity.id));
        
        // Segna che il caricamento iniziale è completato
        initialDataLoadedRef.current = true;
        
        console.log(`[DealNotesSection] Updated processed IDs tracking:`, {
          processedNoteIds: processedNoteIdsRef.current.size,
          processedActivityIds: processedActivityIdsRef.current.size,
          initialDataLoaded: initialDataLoadedRef.current
        });
        
        // Aggiorna i conteggi precedenti
        prevCountsRef.current = {
          notes: dealNotes.length,
          activities: dealActivities.length
        };
      }
    }
  // Usiamo le lunghezze come dipendenze, ma mettiamo un controllo interno
  // per evitare esecuzioni inutili
  }, [dealNotes.length, dealActivities.length]);

  // Listener per gli aggiornamenti da useLoadDeal tramite eventi custom
  useEffect(() => {
    // Gestisce l'evento di avvio/fine controllo aggiornamenti
    const handleCheckingUpdates = (event: CustomEvent) => {
      // Controlla che l'evento sia per questo deal
      if (event.detail.dealId === deal?.id) {
        console.log('[DealNotesSection] Update check status:', event.detail.checking);
        setIsCheckingUpdates(event.detail.checking);
      }
    };
    
    // Gestisce la notifica di aggiornamenti generici di contenuto
    const handleContentUpdated = (event: CustomEvent) => {
      // Controlla che l'evento sia per questo deal
      if (event.detail.dealId === deal?.id) {
        console.log('[DealNotesSection] Content update notification received', event.detail);
        // Non mostreremo una barra di notifica qui perché gli aggiornamenti sono già avvenuti
        // direttamente nello stato del deal - potremmo aggiungere un toast se necessario
      }
    };
    
    // Aggiungi i listener per gli eventi custom
    document.addEventListener('dealCheckingUpdates', handleCheckingUpdates as EventListener);
    document.addEventListener('contentUpdated', handleContentUpdated as EventListener);
    
    // Pulizia
    return () => {
      document.removeEventListener('dealCheckingUpdates', handleCheckingUpdates as EventListener);
      document.removeEventListener('contentUpdated', handleContentUpdated as EventListener);
    };
  }, [deal?.id]);
  
  // Funzione per incorporare i nuovi contenuti nei dati esistenti
  const incorporateNewContent = () => {
    if (!hasNewContent) return;
    
    console.log('[DealNotesSection] Incorporating new content:', newNotes.length, 'notes and', newActivities.length, 'activities');
    
    // 1. Prima incorporiamo le attività
    if (newActivities.length > 0) {
      // Crea una copia delle attività attuali
      const updatedActivities = [...dealActivities];

      // Aggiungi solo le attività che non sono già presenti nella UI
      // usando un controllo più rigoroso per evitare duplicati visibili
      const existingIds = new Set(updatedActivities.map(a => a.id));
      let addedCount = 0;
      
      for (const activity of newActivities) {
        // Verifica molto rigorosa per evitare duplicati:
        // 1. L'ID non deve essere già nelle attività correnti
        // 2. L'ID deve essere già nella reference per evitare duplicati
        if (!existingIds.has(activity.id)) {
          updatedActivities.push(activity);
          addedCount++;
          
          // Aggiungi l'ID all'elenco di attività processate
          processedActivityIdsRef.current.add(activity.id);
        }
      }

      console.log(`[DealNotesSection] Added ${addedCount} new activities`);

      // Usa la callback solo se disponibile E se abbiamo effettivamente
      // aggiunto nuove attività per evitare aggiornamenti inutili
      if (typeof onUpdateActivities === 'function' && addedCount > 0) {
        // Update activities via callback
        onUpdateActivities(updatedActivities);
      } else if (addedCount > 0) {
        // Fallback: mostra un toast per informare l'utente che deve ricaricare la pagina
        // Cannot update activities dynamically without callback
        if (toast) {
          toast.info('Nuove attività disponibili. Ricarica la pagina per visualizzarle.');
        }
      }
    }
    
    // 2. Aggiunge le note all'elenco degli ID processati
    newNotes.forEach(note => {
      processedNoteIdsRef.current.add(note.id);
    });
    
    // Reset dello stato
    setHasNewContent(false);
    setNewNotes([]);
    setNewActivities([]);
    
    // Mostra una notifica - ma solo se abbiamo effettivamente aggiunto qualcosa di nuovo
    if (toast) {
      const noteCount = newNotes.length;
      // Contiamo solo le attività che sono state effettivamente aggiunte all'UI
      const activityCount = newActivities.filter(activity => 
        !dealActivities.some(a => a.id === activity.id)).length;
      
      if (noteCount > 0 && activityCount > 0) {
        toast.success(`Aggiornato con ${noteCount} nuove note e ${activityCount} nuove attività`);
      } else if (noteCount > 0) {
        toast.success(`Aggiornato con ${noteCount} nuove note`);
      } else if (activityCount > 0) {
        toast.success(`Aggiornato con ${activityCount} nuove attività`);
      } else {
        // Se non abbiamo aggiunto nulla di nuovo, mostriamo un messaggio diverso
        toast.info('Contenuto già aggiornato');
      }
    }
  };
  
  // Debug logs e reinizializzazione delle referenze quando cambiano i dati
  useEffect(() => {
    // Solo per il primo render, non ogni volta che dealActivities o dealNotes cambiano
    if (!isInitialRender.current || dealNotes.length > 0 || dealActivities.length > 0) {
      // Potenziamo i log di debug per verificare il caricamento completo
      const filteredActivities = dealActivities.filter((activity: SalesActivity) =>
        showCompletedTasks || activity.status !== 'completed'
      ) || [];

      console.log(`[DealNotesSection] Rendering with ${dealNotes.length} notes and ${dealActivities.length} activities`);
      
      // Se è il primo caricamento completo, reinizializziamo completamente le referenze
      // per evitare che vengano considerati come "nuovi contenuti" elementi già caricati
      if (!isInitialRender.current && (dealNotes.length > 0 || dealActivities.length > 0)) {
        if (!(window as any)._dealNotesInitialRender) {
          (window as any)._dealNotesInitialRender = true;
          
          // Pulizia completa e reinizializzazione durante il primo render
          console.log(`[DealNotesSection] First data render detected - resetting tracking refs`);
          processedNoteIdsRef.current.clear();
          processedActivityIdsRef.current.clear();
          
          // Aggiungi tutti gli ID attuali come "già processati"
          dealNotes.forEach(note => processedNoteIdsRef.current.add(note.id));
          dealActivities.forEach(activity => processedActivityIdsRef.current.add(activity.id));
        }
        
        // Log dettagliati solo per debug, non in produzione
        if (process.env.NODE_ENV === 'development') {
          console.log(`[DealNotesSection] Data details and tracking:`, {
            notes: dealNotes.length,
            activities: dealActivities.length,
            processedNoteIds: processedNoteIdsRef.current.size,
            processedActivityIds: processedActivityIdsRef.current.size
          });
        }
      }

      isInitialRender.current = false;
    }
  // Utilizziamo un controllo più semplice: eseguiamo solo al mount, non ai cambi di dealActivities/dealNotes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return (
    <>
      {/* Allineato a LeadNotesSection: no card container con bordo,
          header uppercase tracking-wide, bottoni testuali Nota/Attivita' */}
      <div className="bg-transparent overflow-hidden h-full flex flex-col">
        <div className="pb-3 mb-3.5 flex items-center gap-2 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] flex-shrink-0">
          <h2 className="hidden lg:flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider bg-gradient-to-r from-[#E91E8C] to-[#F97316] bg-clip-text text-transparent">
            {t.statusUpdate?.history || 'Cronologia note'}

            {/* Count badge related items */}
            {(relatedNotes.length > 0 || relatedActivities.length > 0 || dealRecordings.some(r => r.deal_id !== deal?.id)) && (
              <span className="px-[7px] py-[1px] rounded-full bg-[rgba(233,30,140,0.12)] text-[#E91E8C] text-[10px] font-semibold">
                {relatedNotes.length + relatedActivities.length + dealRecordings.filter(r => r.deal_id !== deal?.id).length}
              </span>
            )}

            {(isCheckingUpdates || loadingRelatedData) && (
              <RefreshCw className="h-3 w-3 text-[#787774] dark:text-gray-500 animate-spin" strokeWidth={1.5} />
            )}
          </h2>

          <div className="flex items-center gap-1 ml-auto">
            {/* Filtri raggruppati, separati dalle azioni da un divisore verticale */}
            {((relatedNotes.length > 0 || relatedActivities.length > 0 || dealRecordings.some(r => r.deal_id !== deal?.id))
              || hasCompletedActivities || clientWa.sessions.length > 0) && (
              <div className="flex items-center gap-0.5 pr-2 mr-1 border-r border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                {/* Toggle related content */}
                {(relatedNotes.length > 0 || relatedActivities.length > 0 || dealRecordings.some(r => r.deal_id !== deal?.id)) && (
                  <button
                    onClick={() => setShowRelatedContent(!showRelatedContent)}
                    className={`inline-flex items-center p-1.5 rounded-md transition-colors ${
                      showRelatedContent
                        ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10'
                        : 'hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10'
                    }`}
                    title={showRelatedContent
                      ? `Nascondi contenuto dal ${relatedNotes[0]?.source_label || relatedActivities[0]?.source_label || 'Lead'}`
                      : `Mostra contenuto dal ${relatedNotes[0]?.source_label || relatedActivities[0]?.source_label || 'Lead'}`}
                  >
                    <User
                      className={`h-3.5 w-3.5 ${showRelatedContent ? 'text-[#D9730D]' : 'text-[#787774] dark:text-gray-400'}`}
                      strokeWidth={1.5}
                    />
                  </button>
                )}

                {/* Filtro layer WhatsApp (vista 7, BETA): accende/spegne le card sessione */}
                {clientWa.sessions.length > 0 && (
                  <button
                    onClick={() => setShowWhatsAppLayer(!showWhatsAppLayer)}
                    className={`inline-flex items-center p-1.5 rounded-md transition-colors ${
                      showWhatsAppLayer
                        ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10'
                        : 'hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10'
                    }`}
                    title={showWhatsAppLayer ? 'Nascondi chat WhatsApp' : 'Mostra chat WhatsApp'}
                  >
                    <MessageCircle
                      className={`h-3.5 w-3.5 ${showWhatsAppLayer ? 'text-[#448361]' : 'text-[#787774] dark:text-gray-400'}`}
                      strokeWidth={1.5}
                    />
                  </button>
                )}

                {/* Toggle completed activities — stessa sorgente del filtro (M25) */}
                {hasCompletedActivities && (
                  <button
                    onClick={() => setShowCompletedTasks(!showCompletedTasks)}
                    className={`inline-flex items-center p-1.5 rounded-md transition-colors ${
                      showCompletedTasks
                        ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10'
                        : 'hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10'
                    }`}
                    title={showCompletedTasks ? (t.statusUpdate?.hideCompleted || "Nascondi attività completate") : (t.statusUpdate?.showCompleted || "Mostra attività completate")}
                  >
                    <Check
                      className={`h-3.5 w-3.5 ${showCompletedTasks ? 'text-[#448361]' : 'text-[#787774] dark:text-gray-400'}`}
                      strokeWidth={1.5}
                    />
                  </button>
                )}
              </div>
            )}

            <div className="flex items-center gap-0.5" data-tour="timeline-add">
              <button
                onClick={onAddNote}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
                title={t.statusUpdate?.addNote || "Aggiungi Nota"}
              >
                <MessageSquare className="h-3.5 w-3.5 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
                {t.statusUpdate?.note || "Nota"}
              </button>

              <button
                onClick={() => onAddTask()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
                title={t.statusUpdate?.addTask || "Aggiungi Attività"}
              >
                <ClipboardList className="h-3.5 w-3.5 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
                {t.statusUpdate?.task || "Attività"}
              </button>

            </div>
          </div>
        </div>

        {/* Composer inline: sopra TUTTO il flusso, subito sotto l'header di sezione.
            `px-3` replica il padding del contenitore scrollabile sotto, così il composer
            condivide i margini con le card della cronologia (come lato lead, dove quel
            contenitore non ha padding e il composer non ne ha). */}
        {composer && deal?.id && (
          <div className="flex-shrink-0 px-3">
            <DealComposerHost
              dealId={deal.id}
              dealLeadId={(deal as any)?.lead_id ?? null}
              dealClientId={deal.client_id ?? null}
              onSaved={composer.onSaved}
              onOpenDrawer={composer.onOpenDrawer}
            />
          </div>
        )}

        <div className="p-3 overflow-y-auto max-h-[calc(100vh-20rem)] md:max-h-[calc(100vh-16rem)]">
          {(loadingNotes || loadingRelatedData) ? (
            <div className="flex justify-center items-center h-20">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#787774]"></div>
            </div>
          ) : dealNotes.length === 0 && dealActivities.length === 0 && dealRecordings.length === 0 && relatedNotes.length === 0 && relatedActivities.length === 0 && clientWa.sessions.length === 0 && !clientWa.loading ? (
            <div className="text-center py-10 text-[13px] text-[#787774] dark:text-white/50">
              <MessageSquare className="h-7 w-7 mx-auto mb-2.5 text-[#9B9A97] dark:text-white/30" strokeWidth={1.5} />
              <p>{t.statusUpdate?.noNotes || 'Nessuna nota disponibile'}</p>
            </div>
          ) : (
            <div>
              {/* Combine activities and notes, then sort them by creation date */}
              {(() => {
                // Collect all items to display
                const allItems = [];
                
                // Add filtered activities from the explicit dealActivities prop
                const filteredActivities = dealActivities
                  .filter((activity: SalesActivity) => showCompletedTasks || activity.status !== 'completed');

                allItems.push(...filteredActivities.map(activity => ({
                  type: 'activity',
                  data: activity,
                  date: activity.created_at || new Date(0).toISOString(),
                  source: 'deal'
                })));
                
                // Add all notes
                allItems.push(...dealNotes.map(note => ({
                  type: 'note',
                  data: note,
                  date: workflowNoteDate(note),
                  source: 'deal'
                })));

                // Add meeting recordings - separate deal-direct from lead/client related
                const directRecordings = dealRecordings.filter(rec => rec.deal_id === deal?.id);
                const relatedRecordings = dealRecordings.filter(rec => rec.deal_id !== deal?.id);

                allItems.push(...directRecordings.map(rec => ({
                  type: 'recording' as const,
                  data: rec,
                  date: rec.created_at,
                  source: 'deal'
                })));

                // Sessioni WhatsApp (vista 7, BETA): a livello cliente dell'offerta.
                // Data = ULTIMO messaggio (una sessione puo' attraversare la mezzanotte:
                // sta sotto la chip giorno dell'ultimo).
                if (showWhatsAppLayer) {
                  allItems.push(...clientWa.sessions.map(session => ({
                    type: 'whatsapp_session' as const,
                    data: session,
                    date: session.endAt,
                    source: 'deal'
                  })));
                }

                // Add related content if enabled
                if (showRelatedContent) {
                  // Add related recordings from lead/client
                  allItems.push(...relatedRecordings.map(rec => ({
                    type: 'recording' as const,
                    data: rec,
                    date: rec.created_at,
                    source: 'related'
                  })));
                  // Add related activities
                  const filteredRelatedActivities = relatedActivities
                    .filter((activity: SalesActivity) => showCompletedTasks || activity.status !== 'completed');

                  allItems.push(...filteredRelatedActivities.map(activity => ({
                    type: 'activity',
                    data: activity,
                    date: activity.created_at || new Date(0).toISOString(),
                    source: 'related'
                  })));

                  // Add related notes  
                  allItems.push(...relatedNotes.map(note => ({
                    type: 'note',
                    data: note,
                    date: note.created_at,
                    source: 'related'
                  })));
                }
                
                // Sort all items by date (newest first)
                const sortedItems = allItems
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

                // Raggruppamento per giorno di calendario: chip separatore stile WhatsApp (B6)
                const dayGroups = groupItemsByDay(sortedItems, (i: any) => i.date);

                const renderItem = (item: any, isFirstCard: boolean = false) => {
                    const isRelated = item.source === 'related';
                    if (item.type === 'activity') {
                      const activity = item.data;
                      return (
                        <div
                          key={`activity-${activity.id}-${item.source}`}
                          className={CARD_CLS}
                          data-tour={isFirstCard ? 'timeline-card' : undefined}
                        >

                          <CardActions>
                            {!isRelated && (activity.status !== 'completed' || onEditActivity || onDeleteActivity) && (
                              <div className="contents">
                                {activity.status !== 'completed' && (
                                  <button onClick={() => handleActivityCompletion(activity)} className={`${ACTION_BTN_CLS} hover:text-[#0F7B6C]`} title={t.statusUpdate?.completeActivity || "Completa Attività"}>
                                    <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                                {onEditActivity && (
                                  <button onClick={() => onEditActivity(activity)} className={`${ACTION_BTN_CLS} hover:text-[#2383E2]`} title={t.statusUpdate?.editActivity || "Modifica Attività"}>
                                    <EditIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                                {onDeleteActivity && (
                                  <button
                                    onClick={() => {
                                      if (activity.google_event_id) {
                                        setActivityToDelete(activity);
                                        setShowDeleteActivityWithCalendarModal(true);
                                      } else {
                                        setItemToDelete(activity.id);
                                        setShowDeleteActivityModal(true);
                                      }
                                    }}
                                    className={`${ACTION_BTN_CLS} hover:text-[#D44C47]`}
                                    title={t.statusUpdate?.deleteActivity || "Elimina Attività"}
                                  >
                                    <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                              </div>
                            )}
                          </CardActions>

                          <CardMedia
                            media={
                              activity.assignee ? (
                                <span className="flex-shrink-0">
                                {(activity.assignee.avatar_url || activity.assignee.profile_image_url) ? (
                                  <img
                                    src={activity.assignee.avatar_url || activity.assignee.profile_image_url}
                                    alt={activity.assignee.full_name || activity.assignee.email || 'Utente'}
                                    className="h-8 w-8 rounded-full object-cover"
                                  />
                                ) : (
                                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F1F1EF] text-[11px] font-semibold text-[#37352F] dark:bg-[#454B4E] dark:text-white">
                                    {(activity.assignee.full_name || activity.assignee.email || 'U')
                                      .split(' ')
                                      .map((part: string) => part[0])
                                      .join('')
                                      .substring(0, 2)
                                      .toUpperCase()}
                                  </span>
                                )}
                              </span>
                              ) : (
                                <span className={mediaTileCls('attivita')}>{getActivityIcon(activity)}</span>
                              )
                            }
                          >
                          <p className="break-words pr-20 text-[13px] font-semibold text-[#37352F] dark:text-white">
                            {activity.title}
                          </p>

                          {/* Meta: autore, assegnatario, scadenza con data compatta (B7) */}
                          <div className={CARD_META_CLS}>
                            {activity.user_id !== activity.assigned_to && activity.users?.full_name && (
                              <span>di {activity.users.full_name}</span>
                            )}
                            {activity.assignee && (
                              <span className="inline-flex items-center gap-1.5">
                                {t.statusUpdate?.assignedTo || 'Assegnato a'} {activity.assignee.full_name || activity.assignee.email}
                              </span>
                            )}
                            {activity.due_date && (
                              <span className="inline-flex items-center gap-1.5 text-[#D9730D] dark:text-[#E89A4B]" title={t.activities?.dueDate || "Data di scadenza"}>
                                <Calendar className="h-3 w-3" strokeWidth={1.5} />
                                Scadenza {formatCompactDate(activity.due_date)}, {formatTime(activity.due_date)}
                              </span>
                            )}
                          </div>

                          {/* Description */}
                          {activity.description && (
                            <div className="mt-2">
                              <ExpandableHtmlContent
                                htmlContent={activity.description}
                                className="text-xs text-black/70 dark:text-white/70 prose prose-sm max-w-none [&_strong]:!text-gray-900 dark:[&_strong]:!text-white [&_b]:!text-gray-900 dark:[&_b]:!text-white"
                              />
                            </div>
                          )}

                          {/* Google Meet Link */}
                          {activity.meet_link && (
                            <div className="mt-2 inline-flex items-center gap-2 group/meet">
                              <a
                                href={activity.meet_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs text-[#2383E2] hover:underline"
                              >
                                <Video className="h-3.5 w-3.5" strokeWidth={1.5} />
                                Unisciti al Meet
                              </a>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(activity.meet_link || ''); toast.success('Link copiato negli appunti'); }}
                                className={`${ACTION_BTN_CLS} hover:text-[#2383E2] opacity-0 group-hover/meet:opacity-100 [@media(hover:none)]:opacity-100`}
                                title="Copia link"
                              >
                                <Copy className="h-3 w-3" strokeWidth={1.5} />
                              </button>
                            </div>
                          )}

                          {/* Outcome */}
                          {activity.status === 'completed' && activity.outcome && (
                            <div className={OUTCOME_WRAP_CLS}>
                              <p className={`${OUTCOME_LABEL_CLS} mb-0.5`}>
                                {t.statusUpdate?.outcome || 'Esito'}:
                              </p>
                              <LinkClickHandler
                                htmlContent={convertUrlsToLinks(activity.outcome)}
                                className="text-xs text-black/70 dark:text-white/70 prose prose-sm max-w-none [&_strong]:!text-gray-900 dark:[&_strong]:!text-white [&_b]:!text-gray-900 dark:[&_b]:!text-white"
                              />
                            </div>
                          )}

                          {/* Completed date */}
                          {activity.completed_at && (
                            <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[#0F7B6C] dark:text-emerald-400">
                              <Check className="h-3 w-3" strokeWidth={1.5} />
                              <span title={t.activities?.completedOn || "Completata il"}>
                                Completata il {formatCompactDate(activity.completed_at)}
                              </span>
                            </p>
                          )}

                          </CardMedia>

                          <TimelineFooter
                            tipo={getActivityTypeLabel(activity.type || 'task', customTypes)}
                            accent="attivita"
                            date={activity.created_at || new Date().toISOString()}
                            titoloData={formatDate(activity.created_at || new Date().toISOString())}
                            left={
                              <>
                                {(activity.status === 'completed' || activity.status === 'overdue') && (
                                  <span className={activity.status === 'completed' ? CHIP_DONE_CLS : CHIP_LATE_CLS}>
                                    {activity.status === 'completed' ? (
                                      <><Check className="h-2.5 w-2.5" strokeWidth={1.5} />{t.statusUpdate?.completed || 'Completata'}</>
                                    ) : (
                                      <><AlertTriangle className="h-2.5 w-2.5" strokeWidth={1.5} />{t.statusUpdate?.overdue || 'In ritardo'}</>
                                    )}
                                  </span>
                                )}
                                {isRelated && (
                                  <span className={`${CHIP_NEUTRAL_CLS} max-w-[180px] truncate`}>
                                    Da: {activity.source_label || 'Lead'}
                                  </span>
                                )}
                                {isRelated && <span className={CHIP_NEUTRAL_CLS}>Solo lettura</span>}
                              </>
                            }
                          />
                        </div>
                      );
                    } else if (item.type === 'recording') {
                      // Wrapper solo per la spaziatura: la card registrazione non ha margine proprio.
                      // Chip di provenienza sulle registrazioni correlate (M16).
                      return (
                        <div key={`recording-${item.data.id}`} className="mb-3">
                          {isRelated && (
                            <div className="mb-1 flex flex-wrap items-center gap-1.5">
                              <span className={CHIP_NEUTRAL_CLS}>
                                Da: {getRecordingSourceLabel(item.data)}
                              </span>
                              <span className={CHIP_NEUTRAL_CLS}>Solo lettura</span>
                            </div>
                          )}
                          <MeetingRecordingCard recording={item.data} compactDate />
                        </div>
                      );
                    } else if (item.type === 'whatsapp_session') {
                      const session = item.data as ClientWhatsAppSession;
                      return (
                        <ClientWhatsAppSessionCard
                          key={`wa-session-${session.key}`}
                          session={session}
                          formatDate={formatDate}
                        />
                      );
                    } else {
                      const note = item.data;
                      const statusChange = note.metadata?.status_change;
                      const hasStatusChange = !!statusChange && statusChange.from !== statusChange.to;

                      // Workflow notes get custom Notion-style rendering
                      if (isWorkflowNote(note)) {
                        return (
                          <WorkflowNoteCard
                            key={`workflow-note-${note.id}`}
                            note={note}
                            formatDate={formatDate}
                          />
                        );
                      }

                      // Email notes (thread Gmail collegato) → card dedicata
                      if (isEmailNote(note)) {
                        return (
                          <EmailNoteCard
                            key={`email-note-${note.id}`}
                            note={note}
                            formatDate={formatDate}
                            onDelete={!isRelated && onDeleteNote ? (id) => { setItemToDelete(id); setShowDeleteNoteModal(true); } : undefined}
                          />
                        );
                      }

                      return (
                        <div
                          key={`note-${note.id}-${item.source}`}
                          className={CARD_CLS}
                          data-tour={isFirstCard ? 'timeline-card' : undefined}
                        >

                          <CardActions>
                            {!isRelated && ((onEditNote && (editingNoteId !== note.id || note.is_edited)) || onDeleteNote) && (
                              <div className="contents">
                                {onEditNote && editingNoteId !== note.id && (
                                  <button
                                    onClick={() => handleEditNote(note)}
                                    className={`${ACTION_BTN_CLS} hover:text-[#2383E2]`}
                                    title={t.statusUpdate?.editNote || "Modifica Nota"}
                                  >
                                    <EditIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                                {onEditNote && note.is_edited && (
                                  <button
                                    onClick={() => handleShowHistory(note.id)}
                                    className={`${ACTION_BTN_CLS} hover:text-[#37352F] dark:hover:text-white`}
                                    title={t.statusUpdate?.viewHistory || "Mostra Cronologia"}
                                  >
                                    <History className="h-3.5 w-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                                {onDeleteNote && (
                                  <button
                                    onClick={() => {
                                      setItemToDelete(note.id);
                                      setShowDeleteNoteModal(true);
                                    }}
                                    className={`${ACTION_BTN_CLS} hover:text-[#D44C47]`}
                                    title={t.statusUpdate?.deleteNote || "Elimina Nota"}
                                  >
                                    <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                                  </button>
                                )}
                              </div>
                            )}
                          </CardActions>

                          <CardMedia media={<span className="flex-shrink-0">
                                {(note.author?.avatar_url || note.author.profile_image_url) ? (
                                  <img
                                    src={note.author?.avatar_url || note.author?.profile_image_url}
                                    alt={note.author?.full_name || note.author?.email || 'Utente'}
                                    className="h-8 w-8 rounded-full object-cover"
                                  />
                                ) : (
                                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F1F1EF] text-[11px] font-semibold text-[#37352F] dark:bg-[#454B4E] dark:text-white">
                                    {(note.author?.full_name || note.author?.email || 'U')
                                      .split(' ')
                                      .map((part: string) => part[0])
                                      .join('')
                                      .substring(0, 2)
                                      .toUpperCase()}
                                  </span>
                                )}
                              </span>}>
                          {/* Identita autore + date secondarie (B7) */}
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pr-20">
                            <span className="text-[13px] font-medium text-[#37352F] dark:text-white">
                              {note.author?.full_name || note.author?.email || (t.statusUpdate?.unknownUser || 'Utente sconosciuto')}
                            </span>
                            {note.is_edited && (
                              <span className="inline-flex items-center gap-1 text-[11px] italic text-[#9B9A97] dark:text-white/40">
                                <Clock className="h-3 w-3" strokeWidth={1.5} />
                                {t.statusUpdate?.edited || 'modificata'}
                              </span>
                            )}
                            {note.updated_at && note.updated_at !== note.created_at && (
                              <span className="text-[11px] text-[#9B9A97] dark:text-white/40">
                                {t.statusUpdate?.updatedAt || 'aggiornata'} {formatCompactDate(note.updated_at)} {formatTime(note.updated_at)}
                              </span>
                            )}
                          </div>

                          {/* Note content - either in edit mode or display mode */}
                          {editingNoteId === note.id && !isRelated ? (
                            <div className="mt-3">
                              <div className="border border-black/10 dark:border-gray-700 rounded-lg overflow-hidden mb-2">
                                <div className="bg-black/5 dark:bg-gray-700/50 px-3 py-1.5 border-b border-black/10 dark:border-gray-700 flex items-center">
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => editor?.chain().focus().toggleBold().run()}
                                      className={`p-1 rounded ${editor?.isActive('bold') ? 'bg-black/10 dark:bg-gray-600' : 'hover:bg-black/5 dark:hover:bg-gray-700/50'}`}
                                    >
                                      <Bold className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => editor?.chain().focus().toggleItalic().run()}
                                      className={`p-1 rounded ${editor?.isActive('italic') ? 'bg-black/10 dark:bg-gray-600' : 'hover:bg-black/5 dark:hover:bg-gray-700/50'}`}
                                    >
                                      <Italic className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => editor?.chain().focus().toggleBulletList().run()}
                                      className={`p-1 rounded ${editor?.isActive('bulletList') ? 'bg-black/10 dark:bg-gray-600' : 'hover:bg-black/5 dark:hover:bg-gray-700/50'}`}
                                    >
                                      <List className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                                      className={`p-1 rounded ${editor?.isActive('orderedList') ? 'bg-black/10 dark:bg-gray-600' : 'hover:bg-black/5 dark:hover:bg-gray-700/50'}`}
                                    >
                                      <ListOrdered className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                                <EditorContent editor={editor} className="p-3 min-h-[100px] max-h-[300px] overflow-y-auto" />
                              </div>
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={handleCancelEdit}
                                  className="px-3 py-1 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md border border-black/10 dark:border-gray-700 text-xs font-medium rounded-md shadow-sm text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-gray-700/50"
                                >
                                  <X className="h-3 w-3 mr-1 inline" />
                                  {t.form?.buttons?.cancel || 'Annulla'}
                                </button>
                                <button
                                  onClick={handleSaveNote}
                                  disabled={isSavingNote}
                                  className={`px-3 py-1 text-xs font-medium rounded-md shadow-sm text-white ${
                                    isSavingNote ? 'bg-green-500/50' : 'bg-green-600 hover:bg-green-700'
                                  }`}
                                >
                                  {isSavingNote ? (
                                    <span className="animate-spin mr-1 inline-block">⏳</span>
                                  ) : (
                                    <Check className="h-3 w-3 mr-1 inline" />
                                  )}
                                  {t.form?.buttons?.save || 'Salva'}
                                </button>
                              </div>

                              {/* Attachments in edit mode */}
                              {!isRelated && (
                                <NoteAttachments
                                  noteId={note.id}
                                  attachments={note.attachments || []}
                                  onUpload={handleUploadNoteAttachment}
                                  onDelete={handleDeleteNoteAttachment}
                                  disabled={isSavingNote}
                                  toast={toast}
                                />
                              )}
                            </div>
                          ) : (
                            <div className="mt-2">
                              <ExpandableHtmlContent
                                htmlContent={note.content}
                                className="text-xs text-black/80 dark:text-white/80 prose prose-sm max-w-none [&_strong]:!text-gray-900 dark:[&_strong]:!text-white [&_b]:!text-gray-900 dark:[&_b]:!text-white"
                              />

                              {/* Attachments in view mode */}
                              {note.attachments && note.attachments.length > 0 && (
                                <div className="mt-2.5">
                                  <NoteAttachments
                                    noteId={note.id}
                                    attachments={note.attachments}
                                    onUpload={handleUploadNoteAttachment}
                                    onDelete={handleDeleteNoteAttachment}
                                    disabled={true}
                                    toast={toast}
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          </CardMedia>

                          <TimelineFooter
                            tipo={t.statusUpdate?.note || 'Nota'}
                            accent="nota"
                            date={note.created_at}
                            titoloData={formatDate(note.created_at)}
                            left={
                              <>
                                {hasStatusChange && (
                                  <span className={STATE_PILL_CLS}>
                                    Stato offerta:
                                    <span className="inline-flex items-center gap-1 font-medium text-[#37352F] dark:text-white">
                                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: DEAL_STATUS_DOT[statusChange.from] || STATE_DOT_FALLBACK }} />
                                      {translateStatus(statusChange.from)}
                                    </span>
                                    <span aria-hidden="true">&rarr;</span>
                                    <span className="inline-flex items-center gap-1 font-medium text-[#37352F] dark:text-white">
                                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: DEAL_STATUS_DOT[statusChange.to] || STATE_DOT_FALLBACK }} />
                                      {translateStatus(statusChange.to)}
                                    </span>
                                  </span>
                                )}
                                {isRelated && (
                                  <span className={`${CHIP_NEUTRAL_CLS} max-w-[180px] truncate`}>
                                    Da: {note.source_label || 'Lead'}
                                  </span>
                                )}
                                {isRelated && <span className={CHIP_NEUTRAL_CLS}>Solo lettura</span>}
                              </>
                            }
                          />
                        </div>
                      );
                    }
                };

                return dayGroups.map((group, groupIndex) => (
                  <div key={group.key}>
                    <DaySeparator label={group.label} dataTour={groupIndex === 0 ? 'timeline-days' : undefined} />
                    {group.items.map((item, itemIndex) => renderItem(item, groupIndex === 0 && itemIndex === 0))}
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </div>
      
      {/* History Modal */}
      <ResponsiveModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        title={t.statusUpdate?.historyTitle || "Cronologia modifiche nota"}
        size="lg"
      >
        <div className="space-y-4">
          {loadingVersions ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black dark:border-white"></div>
            </div>
          ) : noteVersions.length === 0 ? (
            <div className="text-center py-8 text-black/60 dark:text-white/70">
              <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>{t.statusUpdate?.noHistory || 'Nessuna cronologia di modifiche disponibile'}</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {noteVersions.map((version, index) => (
                <div 
                  key={version.id} 
                  className="glass-card p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-sm font-medium">
                        {t.statusUpdate?.versionLabel || 'Versione'} {noteVersions.length - index}
                      </span>
                      <p className="text-xs text-black/60 dark:text-white/70">
                        {formatDate(version.created_at)}
                      </p>
                    </div>
                    {version.author && (
                      <div className="text-xs text-black/70 dark:text-white/80">
                        {t.statusUpdate?.editedBy || 'Modificata da'}: {version.author.full_name || version.author.email || (t.statusUpdate?.unknown || 'Sconosciuto')}
                      </div>
                    )}
                  </div>
                  
                  <div 
                    className="text-sm text-black/80 dark:text-white/90 prose prose-sm dark:prose-invert max-w-none bg-black/5 dark:bg-gray-700 p-3 rounded-md"
                    dangerouslySetInnerHTML={{ __html: version.content }}
                  />
                </div>
              ))}
            </div>
          )}
          
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowHistoryModal(false)}
              className="px-4 py-2 glass-card text-sm font-medium rounded-lg shadow-sm text-black/70 dark:text-white/80 hover:bg-black/5 dark:bg-gray-700 dark:hover:bg-gray-600"
            >
              {t.form?.buttons?.close || 'Chiudi'}
            </button>
          </div>
        </div>
      </ResponsiveModal>
      
      {/* Delete Note Confirmation Modal */}
      <ResponsiveModal
        isOpen={showDeleteNoteModal}
        onClose={() => setShowDeleteNoteModal(false)}
        title={t.statusUpdate?.deleteNoteConfirmTitle || "Elimina nota"}
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-black/80 dark:text-white/90">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <p>{t.statusUpdate?.deleteNoteConfirmText || "Sei sicuro di voler eliminare questa nota? Questa azione non può essere annullata."}</p>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowDeleteNoteModal(false)}
              className="px-4 py-2 glass-card text-sm font-medium rounded-lg shadow-sm text-black/70 dark:text-white/80 hover:bg-black/5 dark:bg-gray-700 dark:hover:bg-gray-600"
            >
              {t.form?.buttons?.cancel || 'Annulla'}
            </button>
            <button
              onClick={() => {
                if (itemToDelete && onDeleteNote) {
                  onDeleteNote(itemToDelete);
                  setShowDeleteNoteModal(false);
                  setItemToDelete(null);
                }
              }}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg shadow-sm hover:bg-red-700"
            >
              {t.statusUpdate?.deleteConfirm || 'Elimina'}
            </button>
          </div>
        </div>
      </ResponsiveModal>
      
      {/* Delete Activity Confirmation Modal */}
      <ResponsiveModal
        isOpen={showDeleteActivityModal}
        onClose={() => setShowDeleteActivityModal(false)}
        title={t.statusUpdate?.deleteActivityConfirmTitle || "Elimina attività"}
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-black/80 dark:text-white/90">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <p>{t.statusUpdate?.deleteActivityConfirmText || "Sei sicuro di voler eliminare questa attività? Questa azione non può essere annullata."}</p>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowDeleteActivityModal(false)}
              className="px-4 py-2 glass-card text-sm font-medium rounded-lg shadow-sm text-black/70 dark:text-white/80 hover:bg-black/5 dark:bg-gray-700 dark:hover:bg-gray-600"
            >
              {t.form?.buttons?.cancel || 'Annulla'}
            </button>
            <button
              onClick={() => {
                if (itemToDelete && onDeleteActivity) {
                  onDeleteActivity(itemToDelete);
                  setShowDeleteActivityModal(false);
                  setItemToDelete(null);
                }
              }}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg shadow-sm hover:bg-red-700"
            >
              {t.statusUpdate?.deleteConfirm || 'Elimina'}
            </button>
          </div>
        </div>
      </ResponsiveModal>

      {/* Delete Activity with Calendar Modal */}
      <DeleteActivityModal
        isOpen={showDeleteActivityWithCalendarModal}
        onClose={() => {
          setShowDeleteActivityWithCalendarModal(false);
          setActivityToDelete(null);
        }}
        onConfirm={async (deleteCalendarEvent) => {
          if (!activityToDelete) return;

          setIsDeletingActivity(true);
          try {
            // Delete Calendar event if requested
            if (deleteCalendarEvent && activityToDelete.google_event_id) {
              const calendarId = activityToDelete.calendar_id || 'primary';
              console.log('[DealNotesSection] Deleting Google Calendar event:', {
                eventId: activityToDelete.google_event_id,
                calendarId
              });

              // deleteEvent now returns true for both successful deletion and 404 (already deleted)
              // It only throws for real errors (permissions, network, etc.)
              await deleteEvent(activityToDelete.google_event_id, calendarId);

              console.log('[DealNotesSection] Calendar event deleted successfully');
            }

            // Delete activity from database
            if (onDeleteActivity) {
              await onDeleteActivity(activityToDelete.id);
            }

            setShowDeleteActivityWithCalendarModal(false);
            setActivityToDelete(null);
            toast.success('Attività eliminata con successo');
          } catch (error: any) {
            console.error('[DealNotesSection] Error deleting activity:', error);

            // Show user-friendly error message based on error type
            if (error?.message?.includes('403') || error?.message?.includes('Forbidden')) {
              toast.error('Errore: permessi Google Calendar insufficienti. Riconnetti l\'account dalle impostazioni.');
            } else {
              toast.error('Errore durante l\'eliminazione dell\'attività: ' + (error?.message || 'Errore sconosciuto'));
            }
          } finally {
            setIsDeletingActivity(false);
          }
        }}
        activity={activityToDelete}
        isDeleting={isDeletingActivity}
      />

      {/* Task Completion Modal */}
      {selectedActivity && (
        <TaskCompletionModal
          isOpen={showTaskCompletionModal}
          onClose={() => {
            setShowTaskCompletionModal(false);
            setSelectedActivity(null);
          }}
          onComplete={submitActivityCompletion}
          task={{
            id: selectedActivity.id,
            title: selectedActivity.title,
            description: selectedActivity.description || '',
            type: selectedActivity.type || 'task',
            status: selectedActivity.status,
            due_date: selectedActivity.due_date,
            user_id: selectedActivity.user_id
          }}
        />
      )}

    </>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(DealNotesSection);